import React, { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Menu, X, Calendar, Phone, Mail, MapPin, Award } from 'lucide-react';

const BOOKING_LINK = "https://system.easypractice.net/book/akupunktur-klinikken-acu-sense";

export default function Layout({ children }) {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const { pathname } = useLocation();

  // Scroll to top on route change
  useEffect(() => {
    window.scrollTo(0, 0);
    setIsMenuOpen(false);
  }, [pathname]);

  const navItems = [
    { label: 'Forside', path: '/' },
    { label: 'Behandlinger', path: '/behandlinger' },
    { label: 'Prisliste', path: '/priser' },
    { label: 'Om klinikken', path: '/om' },
    { label: 'Kontakt', path: '/kontakt' }
  ];

  const isActive = (path) => {
    if (path === '/') {
      return pathname === '/';
    }
    return pathname.startsWith(path);
  };

  return (
    <div className="min-h-screen flex flex-col bg-[#FAF9F6] text-[#2C3531] font-sans antialiased selection:bg-[#8FA89B] selection:text-white">
      {/* Sticky Header with Announcement Bar */}
      <header className="sticky top-0 z-50 transition-all duration-300">
        {/* Top Announcement Bar */}
        <div className="bg-[#4E6052] text-[#FAF9F6] text-xs sm:text-sm py-2.5 px-4 text-center border-b border-[#3D4F44]">
          <div className="max-w-7xl mx-auto flex items-center justify-center gap-2 flex-wrap">
            <span className="inline-flex items-center gap-1.5 font-semibold bg-white/20 px-2.5 py-0.5 rounded-full text-[11px] sm:text-xs uppercase tracking-wider">
              <MapPin className="w-3.5 h-3.5 text-[#C4D7CB]" />
              Ny adresse
            </span>
            <span>
              Fra 1. september 2026 flytter jeg klinikken til anden adresse: <strong className="font-semibold text-white">Banealle 8F, 8450 Hammel</strong>
            </span>
          </div>
        </div>

        {/* Main Navigation Bar */}
        <div className="bg-[#FAF9F6]/90 backdrop-blur-md border-b border-[#E6E4DF]">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="flex items-center justify-between h-20">
            {/* Logo */}
            <Link to="/" className="flex items-center gap-3 group">
              <img 
                src="https://assets.auxodev.dk/cdn-cgi/image/width=120,quality=90,format=auto/assets/2c4bccc2-b14e-4d92-a991-09327bae6f21/logo-til-hjemmeside.jpeg" 
                alt="Klinik Acu-sense Logo" 
                className="w-12 h-12 rounded-full object-cover border border-[#E6E4DF] transition-transform duration-300 group-hover:scale-105"
              />
              <div className="flex flex-col">
                <span className="font-serif text-xl font-bold tracking-wide text-[#2C3531] group-hover:text-[#5F7464] transition-colors duration-200">
                  Klinik Acu-sense
                </span>
                <span className="text-[10px] uppercase tracking-[0.2em] text-[#5F7464] font-medium">
                  RAB-godkendt akupunktur
                </span>
              </div>
            </Link>

            {/* Desktop Navigation (Hidden below 1200px) */}
            <nav className="hidden min-[1200px]:flex items-center gap-8">
              {navItems.map((item) => (
                <Link
                  key={item.path}
                  to={item.path}
                  className={`text-sm font-medium tracking-wide transition-colors duration-200 relative py-2 ${
                    isActive(item.path)
                      ? 'text-[#5F7464] font-semibold'
                      : 'text-[#5C6360] hover:text-[#2C3531]'
                  }`}
                >
                  {item.label}
                  {isActive(item.path) && (
                    <span className="absolute bottom-0 left-0 right-0 h-0.5 bg-[#5F7464] rounded-full" />
                  )}
                </Link>
              ))}
            </nav>

            {/* Desktop CTA (Hidden below 1200px) */}
            <div className="hidden min-[1200px]:flex items-center gap-4">
              <a
                href={BOOKING_LINK}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center justify-center px-5 py-2.5 rounded-full bg-[#5F7464] text-white text-sm font-medium tracking-wide hover:bg-[#4E6052] transition-all duration-200 shadow-sm hover:shadow-md"
              >
                <Calendar className="w-4 h-4 mr-2" />
                Book tid online
              </a>
            </div>

            {/* Mobile Menu Button (Shown below 1200px) */}
            <div className="flex min-[1200px]:hidden">
              <button
                onClick={() => setIsMenuOpen(!isMenuOpen)}
                className="inline-flex items-center justify-center p-2 rounded-md text-[#5C6360] hover:text-[#2C3531] hover:bg-[#E6E4DF] transition-colors duration-200 focus:outline-none"
                aria-expanded="false"
              >
                <span className="sr-only">Åbn menu</span>
                {isMenuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
              </button>
            </div>
          </div>
        </div>

        {/* Mobile Navigation Menu (Shown below 1200px) */}
        {isMenuOpen && (
          <div className="min-[1200px]:hidden bg-[#FAF9F6] border-b border-[#E6E4DF] animate-in slide-in-from-top duration-200">
            <div className="px-4 pt-2 pb-6 space-y-2">
              {navItems.map((item) => (
                <Link
                  key={item.path}
                  to={item.path}
                  className={`block px-4 py-3 rounded-lg text-base font-medium transition-colors duration-200 ${
                    isActive(item.path)
                      ? 'bg-[#E6E4DF] text-[#2C3531] font-semibold'
                      : 'text-[#5C6360] hover:bg-[#F3F1EC] hover:text-[#2C3531]'
                  }`}
                >
                  {item.label}
                </Link>
              ))}
              <div className="pt-4 px-4">
                <a
                  href={BOOKING_LINK}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="w-full inline-flex items-center justify-center px-5 py-3 rounded-full bg-[#5F7464] text-white text-base font-medium tracking-wide hover:bg-[#4E6052] transition-colors duration-200 shadow-sm"
                >
                  <Calendar className="w-5 h-5 mr-2" />
                  Book tid online
                </a>
              </div>
            </div>
          </div>
        )}
        </div>
      </header>

      {/* Main Content */}
      <main className="flex-grow">
        {children}
      </main>

      {/* Footer */}
      <footer className="bg-[#F3F1EC] border-t border-[#E6E4DF] text-[#5C6360]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12 md:py-16">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-10 md:gap-8">
            {/* Column 1: Brand */}
            <div className="space-y-4">
              <div className="flex items-center gap-3">
                <img 
                  src="https://assets.auxodev.dk/cdn-cgi/image/width=120,quality=90,format=auto/assets/2c4bccc2-b14e-4d92-a991-09327bae6f21/logo-til-hjemmeside.jpeg" 
                  alt="Klinik Acu-sense Logo" 
                  className="w-14 h-14 rounded-full object-cover border border-[#E6E4DF]"
                />
                <span className="font-serif text-lg font-bold tracking-wide text-[#2C3531]">
                  Klinik Acu-sense
                </span>
              </div>
              <p className="text-sm leading-relaxed max-w-sm">
                Professionel akupunktur med fokus på ro, faglighed og individuel behandling i Hammel. RAB-godkendt akupunktør Brian Hangaard.
              </p>
              <div className="flex items-center gap-2 text-xs text-[#5F7464] font-semibold">
                <Award className="w-4 h-4" />
                <span>RAB-godkendt akupunktør</span>
              </div>
            </div>

            {/* Column 2: Navigation Links */}
            <div>
              <h3 className="font-serif text-sm font-bold tracking-wider text-[#2C3531] uppercase mb-4">
                Hurtige links
              </h3>
              <ul className="space-y-2.5 text-sm">
                {navItems.map((item) => (
                  <li key={item.path}>
                    <Link
                      to={item.path}
                      className="hover:text-[#2C3531] transition-colors duration-200"
                    >
                      {item.label}
                    </Link>
                  </li>
                ))}
                <li>
                  <a
                    href={BOOKING_LINK}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-[#5F7464] hover:text-[#4E6052] font-medium transition-colors duration-200"
                  >
                    Online booking &rarr;
                  </a>
                </li>
              </ul>
            </div>

            {/* Column 3: Contact Info */}
            <div className="space-y-4">
              <h3 className="font-serif text-sm font-bold tracking-wider text-[#2C3531] uppercase mb-4">
                Kontaktoplysninger
              </h3>
              <ul className="space-y-3 text-sm">
                <li className="flex items-start gap-3">
                  <MapPin className="w-5 h-5 text-[#5F7464] shrink-0 mt-0.5" />
                  <span>
                    Klinik Acu-sense<br />
                    Banealle 8F, 8450 Hammel
                  </span>
                </li>
                <li className="flex items-center gap-3">
                  <Phone className="w-5 h-5 text-[#5F7464] shrink-0" />
                  <span className="whitespace-nowrap">Tlf: 2045 6563</span>
                </li>
                <li className="flex items-center gap-3">
                  <Mail className="w-5 h-5 text-[#5F7464] shrink-0" />
                  <span className="whitespace-nowrap">Email: info@acu-sense.dk</span>
                </li>
              </ul>
              <div className="pt-2 text-xs text-[#8A908D]">
                CVR: 40203850
              </div>
            </div>
          </div>

          {/* Bottom Bar */}
          <div className="mt-12 pt-8 border-t border-[#E6E4DF] flex flex-col sm:flex-row items-center justify-between gap-4 text-xs">
            <p>&copy; {new Date().getFullYear()} Klinik Acu-sense. Alle rettigheder forbeholdes.</p>
            
            <div className="flex items-center gap-3 text-[#5C6360]">
              <a 
                href="https://www.iubenda.com/privacy-policy/73888281/cookie-policy" 
                className="iubenda-nostyle iubenda-noiframe iubenda-embed hover:text-[#2C3531] transition-colors duration-200" 
                title="Cookiepolitik"
              >
                Cookiepolitik
              </a>
              <span className="text-[#E6E4DF] select-none">|</span>
              <a 
                href="https://www.iubenda.com/privacy-policy/73888281" 
                className="iubenda-nostyle iubenda-noiframe iubenda-embed hover:text-[#2C3531] transition-colors duration-200" 
                title="Privatlivspolitik"
              >
                Privatlivspolitik
              </a>
            </div>

            <p className="flex items-center gap-1">
              <span>Udviklet med fokus på ro og faglighed</span>
            </p>
          </div>
        </div>
      </footer>
    </div>
  );
}
